import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ChevronRight, LogOut, LayoutDashboard } from "lucide-react";
import { BRAND } from "../lib/brand";
import { useAuth } from "../context/AuthContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/courier", label: "Courier" },
  { to: "/sourcing", label: "Sourcing" },
  { to: "/pickup", label: "Pickup" },
  { to: "/quote", label: "Quote" },
  { to: "/forms", label: "Forms" },
  { to: "/track", label: "Track" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200" data-testid="site-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
          <img src={BRAND.logo} alt="Amplartic Logistics" className="h-12 w-12 object-contain" />
          <div className="leading-tight hidden sm:block">
            <div className="font-display font-extrabold text-[#1E2A78] tracking-tight text-base">AMPLARTIC</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Logistics Inc.</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-2.5 xl:px-3 py-2 text-sm font-medium rounded-sm transition-colors ${
                  isActive ? "text-[#1E2A78] bg-[#FFD700]/20" : "text-slate-700 hover:text-[#1E2A78] hover:bg-slate-100"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {user && user !== false ? (
            <>
              <button
                onClick={() => nav("/admin")}
                data-testid="navbar-dashboard-btn"
                className="px-3 py-2 text-sm font-medium rounded-sm bg-[#1E2A78] text-white hover:bg-[#17205E] inline-flex items-center gap-2"
              >
                <LayoutDashboard className="h-4 w-4" /> Dashboard
              </button>
              <button
                onClick={() => { logout(); nav("/"); }}
                data-testid="navbar-logout-btn"
                className="px-3 py-2 text-sm font-medium rounded-sm text-slate-700 hover:text-[#1E2A78] inline-flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </>
          ) : (
            <Link
              to="/track"
              data-testid="navbar-track-cta"
              className="px-4 py-2 text-sm font-semibold rounded-sm bg-[#FFD700] text-[#1E2A78] hover:bg-[#E6C200] inline-flex items-center gap-1 whitespace-nowrap"
            >
              Track Shipment <ChevronRight className="h-4 w-4" />
            </Link>
          )}
        </div>

        <button
          className="lg:hidden p-2 rounded-sm hover:bg-slate-100"
          onClick={() => setOpen(!open)}
          data-testid="navbar-menu-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200" data-testid="navbar-mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `block px-3 py-2 text-sm font-medium rounded-sm ${
                    isActive ? "text-[#1E2A78] bg-[#FFD700]/20" : "text-slate-700 hover:bg-slate-100"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {user && user !== false ? (
              <>
                <button
                  onClick={() => { setOpen(false); nav("/admin"); }}
                  className="w-full text-left px-3 py-2 text-sm font-medium rounded-sm bg-[#1E2A78] text-white"
                >
                  Dashboard
                </button>
                <button
                  onClick={() => { setOpen(false); logout(); nav("/"); }}
                  className="w-full text-left px-3 py-2 text-sm font-medium text-slate-700"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/track"
                onClick={() => setOpen(false)}
                className="block px-3 py-2 text-sm font-semibold rounded-sm bg-[#FFD700] text-[#1E2A78]"
              >
                Track Shipment
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
